import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import {
  type ExportError,
  type ShareExport,
  type ShareExportRequest,
  TERMINAL_STATUSES,
  isExportExpiredError,
  isKnownExportError,
} from '@/types/export';
import { createShareExport, getShareExport } from '@/services/export.service';
import { useExportStore } from '@/hooks/data/export/useExportStore';
import { STALE, useLatestAsync } from '@/hooks/useLatestAsync';
import { useCapabilities } from '@/hooks/useCapabilities';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { download } from '@/lib/download';
import { useTranslations } from 'next-intl';

type ShareExportMutationError = Record<string, unknown> & { status: number };

/**
 * Guest-facing counterpart to useExportDownload. Calls the share-scoped export
 * endpoints — OTP guests and authenticated tenant members only; the
 * canDownloadAssets guard above blocks anonymous callers before the request is
 * ever made (they'd 401 regardless, per the backend's forbid-anonymous-export
 * rule). Tracks progress via the persistent manager mounted in _app.tsx, and
 * reuses the shared export store + download utility.
 */
export function useShareExportDownload(shareId: string) {
  const tCommon = useTranslations('common');
  const tDownloadZip = useTranslations('dialogs.downloadZip');
  const tExport = useTranslations('toasts.export');
  const tErrors = useTranslations('errors');
  const { toast } = useToast();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const addExport = useExportStore((state) => state.addExport);
  const { canDownloadAssets } = useCapabilities();

  const [dryRunExport, setDryRunExport] = useState<ShareExport | null>(null);
  const [dryRunError, setDryRunError] = useState<ExportError | null>(null);
  const [isExpired, setIsExpired] = useState(false);

  // The two mutationFns are guarded by canDownloadAssets above; downloadExport
  // (below) is guarded by isAuthenticated instead, since it's also reachable
  // from the out-of-band /exports/[id]/file redirect page, which isn't
  // classified as a share page by useCapabilities — isAuthenticated matches
  // the backend's actual rule for that endpoint (anonymous denied, any other
  // identity allowed) without excluding OTP guests the way canDownloadAssets
  // would off a /shares/... page.
  const resolveToken = useCallback(
    () => (isAuthenticated ? getAccessTokenSilently().catch(() => undefined) : Promise.resolve(undefined)),
    [isAuthenticated, getAccessTokenSilently],
  );

  // Progress is tracked by the persistent manager mounted in _app.tsx, not here: this
  // hook lives in the download dialog, which unmounts once the export starts.

  // Both flows surface their own errors (the dry-run yellow banner + the scoped
  // toasts in createExport below), so each mutation defines its own onError to
  // override the global mutation error toast (_app.tsx defaultOptions). Without it,
  // a known error like nothing_to_export would show the banner AND a duplicate
  // global toast — the authenticated dialog avoids this the same way, via
  // useCreateExportMutation (which always passes an onError).
  const { mutateAsync: dryRunMutateAsync } = useMutation<ShareExport, ShareExportMutationError, ShareExportRequest>({
    mutationFn: async (request) =>
      createShareExport(
        shareId,
        { ...request, variantIds: request.variantIds ?? [], dryRun: true },
        await resolveToken(),
      ),
    onError: () => {},
  });
  const { mutateAsync: createMutateAsync, isPending: downloadIsPending } = useMutation<
    ShareExport,
    ShareExportMutationError,
    ShareExportRequest
  >({
    mutationFn: async (request) =>
      createShareExport(
        shareId,
        { ...request, variantIds: request.variantIds ?? [], dryRun: false },
        await resolveToken(),
      ),
    onError: () => {},
  });

  const withLatestDryRun = useLatestAsync<ShareExport>();
  const withLatestRealExport = useLatestAsync<ShareExport>();

  const createDryRunExport = useCallback(
    async (request: ShareExportRequest) => {
      if (!canDownloadAssets) {
        return;
      }
      setDryRunExport(null);
      try {
        const result = await withLatestDryRun(dryRunMutateAsync(request));
        if (result === STALE) {
          return;
        }
        setDryRunError(null);
        setDryRunExport(result);
      } catch (error: any) {
        if (isKnownExportError(error)) {
          setDryRunError(error);
        } else {
          setDryRunError({
            code: 'unknown',
            message: error.message,
            status: error.status ?? 0,
            assetsCount: 0,
            estimatedSizeBytes: 0,
          });
          toast({
            variant: ToastVariant.DESTRUCTIVE,
            title: tCommon('error'),
            description: tDownloadZip('summaryErrorDescription'),
          });
        }
      }
    },
    [canDownloadAssets, dryRunMutateAsync, withLatestDryRun, tCommon, tDownloadZip, toast],
  );

  const createExport = useCallback(
    async (request: ShareExportRequest, label: string) => {
      if (!canDownloadAssets) {
        return;
      }
      try {
        const result = await withLatestRealExport(createMutateAsync(request));
        if (result === STALE) {
          return;
        }
        if (result.status === 'nothing_to_export') {
          toast({ title: tExport('nothingToExport.title'), description: tExport('nothingToExport.description') });
        } else if (result.status === 'failed') {
          toast({
            variant: ToastVariant.DESTRUCTIVE,
            title: tExport('failed.title'),
            description: tExport('failed.description'),
          });
        } else if (!TERMINAL_STATUSES.includes(result.status)) {
          // Record the share so the progress manager reconciles this export against
          // its own share, not whichever share the viewer navigates to later.
          addExport(result.id, label, shareId);
        }
      } catch (error: any) {
        if (error.status === 422) {
          toast({ title: tExport('nothingToExport.title'), description: tExport('nothingToExport.description') });
        } else {
          toast({
            variant: ToastVariant.DESTRUCTIVE,
            title: tExport('failed.title'),
            description: tExport('failed.description'),
          });
        }
      }
    },
    [canDownloadAssets, createMutateAsync, withLatestRealExport, tExport, toast, addExport, shareId],
  );

  const downloadExport = async (exportId: string, shareIdOverride?: string): Promise<void> => {
    if (!isAuthenticated) {
      return;
    }
    try {
      const token = await resolveToken();
      const exportData = await getShareExport({ shareId: shareIdOverride ?? shareId, id: exportId }, token);
      const filename = exportData.name ? `${exportData.name}.zip` : `export-${exportId}.zip`;
      await download(exportData.links.download, filename, token);
    } catch (error) {
      if (isExportExpiredError(error)) {
        setIsExpired(true);
        toast({ variant: ToastVariant.DESTRUCTIVE, title: tCommon('error'), description: tErrors('exportExpired') });
        return;
      }
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: tErrors('toastDefaultDescription'),
      });
    }
  };

  return {
    createDryRunExport,
    createExport,
    downloadIsPending,
    dryRunExport,
    dryRunError,
    downloadExport,
    isExpired,
  };
}
